import {
    NullOr,
    PROGRESSIVE_DIALER_DISPOSITIONS,
    RVTask,
    TASK_RESERVATION_STATUSES,
    TWO_TASK_PAIR_TYPES,
} from "@regal-voice/shared-types";
import camelCase from "lodash/camelCase";

import {
    CHANNELS_TO_EVALUATE_INBOUND_TASK,
    TASK_LIST_EVALUATE_CALL_CHANNELS,
} from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/constants";
import { taskSummary } from "Services/ConversationsApiService";
import { deepCaseConversion } from "Services/HelpersService";
import { getLogger } from "Services/LoggingService";
import {
    removeConnectionQualityStatistics,
    selectConnectionQualityStatistics,
} from "Services/state/conferences/ConnectionQualityStateSlice";
import { reduxStore } from "Services/state/Storage";
import { Brand } from "Types/Brand";

import type { AgentInformationState } from "Services/state/agent/AgentInformationSlice";
import type { TaskAttributes } from "Types/TaskTypes/TaskAttributes";

// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
export enum Direction {
    outbound = "outbound",
    inbound = "inbound",
}

const logger = getLogger("Legacy transfer");

export function hasContactPhone(task: NullOr<RVTask>): boolean {
    return !!(task && getAttributes(task).contactPhone);
}

export function isPendingInboundConferenceTask(task?: NullOr<RVTask>): boolean {
    if (!task) {
        return false;
    }
    return (
        CHANNELS_TO_EVALUATE_INBOUND_TASK.includes(task?.taskChannelUniqueName ?? "") &&
        task?.attributes.direction == "inbound" &&
        !task?.attributes.triggerCall &&
        task?.status == "pending"
    );
}

export const isTaskACall = (task?: NullOr<RVTask>): boolean => {
    return !!task && TASK_LIST_EVALUATE_CALL_CHANNELS.includes(task.taskChannelUniqueName);
};

export function isPendingConferenceTask(task: NullOr<RVTask>): boolean {
    return CHANNELS_TO_EVALUATE_INBOUND_TASK.includes(task?.taskChannelUniqueName ?? "") && task?.status == "pending";
}

export function isInboundConferenceCall(task: RVTask | undefined): boolean {
    return (
        CHANNELS_TO_EVALUATE_INBOUND_TASK.includes(task?.taskChannelUniqueName ?? "") &&
        task?.attributes.direction == "inbound" &&
        !task?.attributes.triggerCall
    );
}

export function isEarlyCustomerHangUpProgressiveDialCall(task?: Partial<RVTask<"unknown">>): boolean {
    return !!(
        task?.attributes?.taskType === "Progressive Dial" &&
        task?.attributes?.disposition === PROGRESSIVE_DIALER_DISPOSITIONS.NO_CONVERSATION_CUSTOMER_HUNG_UP
    );
}

export function isAcceptedInboundConferenceTask(task: NullOr<RVTask>): boolean {
    return (
        CHANNELS_TO_EVALUATE_INBOUND_TASK.includes(task?.taskChannelUniqueName ?? "") &&
        task?.attributes.direction == "inbound" &&
        !task?.attributes.triggerCall &&
        task.status == "accepted"
    );
}

export function isAcceptedConferenceTask(task: NullOr<RVTask>): boolean {
    return CHANNELS_TO_EVALUATE_INBOUND_TASK.includes(task?.taskChannelUniqueName ?? "") && task?.status == "accepted";
}

export function isConnectedProgressiveDialerTask(task: RVTask | undefined): boolean {
    return !!task && task.attributes.taskType === "Progressive Dial" && task.status === "accepted";
}

export function isTransferCallTask(task?: RVTask): task is RVTask {
    return !!task && task.attributes.taskType === "Transfer Call";
}

export function isTakeOverCall(task?: RVTask): task is RVTask {
    return !!task && task.attributes.taskType === "Takeover Call";
}

export function isPendingInboundTransferTask(task?: RVTask): boolean {
    return isTransferCallTask(task) && task.status === TASK_RESERVATION_STATUSES.PENDING;
}

/**
 * Returns true when this task is the first in a two-task flow.
 * Based on task type and absenece of original task sid attribute
 */
export function isFirstOfTwoTaskPair(task?: RVTask): boolean {
    return !!task && TWO_TASK_PAIR_TYPES.includes(task.attributes.taskType) && !task.attributes.original_task_sid;
}

/**
 * Returns true when this task is the second in a two-task flow — i.e. it was spawned from an
 * existing task and carries an original task sid pointing to the first task.
 *
 * Only applies to task types that participate in two-task flows.
 */
export function isSecondOfTwoTaskPair(task?: RVTask): boolean {
    return !!task && TWO_TASK_PAIR_TYPES.includes(task.attributes.taskType) && !!task.attributes.original_task_sid;
}

export function getAttributes(task?: RVTask): TaskAttributes {
    if (task) {
        const attributes =
            (deepCaseConversion(task.attributes, (key: string) => camelCase(key)) as TaskAttributes) || {};
        attributes.originalTaskSid = attributes.originalTaskSid || task.taskSid;

        return attributes;
    }

    return {} as TaskAttributes;
}

export async function summarizeTask(
    values: {
        task: RVTask;
        brand: Brand;
        outcomeId?: string;
        disposition: string;
        objections?: Array<string>;
        notes?: string;
        dispositionedBy: "agent" | "system";
        regalVoicePhoneFriendlyName: string;
        agent: AgentInformationState;
    },
    taskSummarySubmitted = false
): Promise<boolean> {
    const {
        disposition,
        outcomeId,
        objections,
        notes,
        dispositionedBy,
        task,
        brand,
        regalVoicePhoneFriendlyName,
        agent,
    } = values;

    if (!task) {
        logger.error("Task is undefined", { brand });
        return false;
    }

    if (!brand) {
        logger.error("Brand is undefined", { task });
        return false;
    }

    const { contactPhone, email, originalTaskSid, proxySession, regalVoicePhone, campaignInfo } = getAttributes(task);
    if (taskSummarySubmitted) {
        logger.warn(`Attempting to resubmit task summary: ${task.sid}`);
        return false;
    }

    if (!contactPhone && !email) {
        return false;
    }

    const audioStatistics = selectConnectionQualityStatistics(reduxStore.getState())[task.taskSid];
    const taskSummaryData = {
        phone: contactPhone,
        email,
        disposition,
        outcomeId,
        agentID: agent.attributes?.contact_uri,
        agentEmail: agent.email as string,
        objections,
        dispositionedBy,
        notes,
        sid: task.sid,
        taskAttributes: {
            accountSid: brand.accountSid,
            taskAge: task.age,
            taskSid: task.taskSid,
            originalTaskSid,
            regalVoicePhone,
            regalVoicePhoneFriendlyName,
            proxySession,
            campaignInfo,
            taskChannelUniqueName: task.taskChannelUniqueName,
            taskDateCreated: task.createdDate,
            taskPriority: task.priority,
            taskQueueName: task.queueName,
            workflowName: task.workflowName,
            workerAttributes: agent.attributes as Record<string, any>,
            contactPhone: contactPhone,
            email,
            ...task.attributes,
        },
        audioStatistics,
    };

    const response = await taskSummary(taskSummaryData);
    reduxStore.dispatch(removeConnectionQualityStatistics(task.taskSid));
    return response?.success;
}
